import Provider, {
  ApiInteressentSubmitData,
  ApiProvider,
  DetailProviderFilters,
  ProviderFilters,
} from '@/model/Provider';
import {CombinedApiResponse, doApiRequest, Method, Module} from '..';
import {parseGenericFilters} from '../meta/filters';
import {ApiSorts, ApiSortsDerived} from '../meta/odata';
import ProviderDistance, {ApiProviderDistance} from '@/model/ProviderDistance';
import {getCurrentPosition} from '@/util';

export enum ResourceKunde {
  Kunde = '/Kunde',
}

export enum ExpandKunde {
  RechnungAnschrift = 'RechnungAnschrift',
  ZahlungsBedingungen = 'ZahlungsBedingungen',
  PostAnschrift = 'PostAnschrift',
  Adressen = 'Adressen',
}

export enum CustomerAction {
  Umkreissuche = '/action/umkreissuche',
  Interessentanlegen = '/action/interessentanlegen',
  DateiHochladen = '/{kundennr}/action/datei/hochladen',
}

export async function getProviders(
    filters: ProviderFilters,
    sorts: ApiSortsDerived<ApiProvider>,
    limit: number | null = null,
    skip: number = 0,
): Promise<CombinedApiResponse<Provider[]>> {
  const response = await doApiRequest<ApiProvider[]>({
    method: Method.Get,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    filters: parseGenericFilters({...filters}),
    select: new Set(['KundenNr', 'Kundname', 'KundStatus']),
    expand: {
      [ExpandKunde.PostAnschrift]: {
        select: new Set([
          'Plz',
          'Ort',
        ]),
      },
    },
    sorts,
    skip,
    limit,
  });

  return [response.Data.map((p) => Provider.fromApi(p)), response.CountAll];
}

export async function getDetailProvider(
    filters: DetailProviderFilters,
): Promise<CombinedApiResponse<Provider | null>> {
  const response = await doApiRequest<ApiProvider[]>({
    method: Method.Get,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    filters: parseGenericFilters({...filters}),
    select: new Set(['KundenNr', 'Kundname', 'KundStatus', 'DebitorKto', 'KreditorKto']),
    expand: {
      [ExpandKunde.PostAnschrift]: {
        select: new Set([
          'Telefon1',
          'Telefon2',
          'Plz',
          'Ort',
          'Strasse',
          'Nummer',
          'Email',
          'Homepage',
        ]),
      },
    },
  });

  return response.Data.length ? [Provider.fromApi(response.Data[0])] : [null];
}

export async function getNearbyProviders(
    filters: object,
    sorts: ApiSorts,
    limit: number | null = null,
    skip: number = 0,
): Promise<CombinedApiResponse<ProviderDistance[]>> {
  console.time('get geolocation');
  const geolocation = await getCurrentPosition();
  console.timeEnd('get geolocation');
  const response = await doApiRequest<ApiProviderDistance[]>({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: CustomerAction.Umkreissuche,
    filters: parseGenericFilters({...filters}),
    expand: {
      'Kunde': {
        select: new Set(['KundenNr', 'Kundname', 'KundStatus']),
        expand: {
          [ExpandKunde.PostAnschrift]: {
            select: new Set([
              'Plz',
              'Ort',
            ]),
          },
        },
      },
    },
    sorts,
    skip,
    limit,
    data: {
      Latitude: geolocation.coords.latitude,
      Longitude: geolocation.coords.longitude,
    },
  });

  return [response.Data.map((p) => ProviderDistance.fromApi(p)), response.CountAll];
}

export async function getProviderDocuments(
    filters: DetailProviderFilters,
    sorts?: ApiSortsDerived<ApiProvider>,
    limit: number | null = null,
    skip: number = 0,
): Promise<CombinedApiResponse<Provider | null>> {
  const response = await doApiRequest<ApiProvider[]>({
    method: Method.Get,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    filters: parseGenericFilters({...filters}),
    select: new Set(['KundenNr', 'KundStatus', 'Kundname', 'Dateien', 'Unterlagen']),
    sorts,
    skip,
    limit,
  });
  return response.Data.length ? [Provider.fromApi(response.Data[0])] : [null];
}

export async function postProviderDocuments(
    kundennr: number,
    data: FormData,
) {
  const response = await doApiRequest({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: CustomerAction.DateiHochladen,
    actionParameters: {
      kundennr,
    },
    data,
    limit: 0,
  });
  return response;
}
export async function postProvider(
    data: ApiInteressentSubmitData,
): Promise<Provider> {
  const response = await doApiRequest<ApiProvider[]>({
    method: Method.Post,
    resource: ResourceKunde.Kunde,
    module: Module.Kunde,
    action: CustomerAction.Interessentanlegen,
    data: [data],
  });

  return Provider.fromApi(response.Data[0]);
}
